/**
 * Module-level store for an in-flight bulk model import.
 *
 * The ZIP/folder upload loop is a plain async function, so it keeps running
 * when the import page unmounts. Previously its progress lived in component
 * state, so navigating away made the run invisible and un-cancellable. Keeping
 * the run state here (outside React) means the upload survives route changes:
 * a floating dock can render progress anywhere in the app, and returning to
 * /agency/import re-attaches to the live run.
 *
 * A tab close or hard refresh still stops the upload - the existing resume
 * flow picks it back up from the interrupted job.
 */

export interface ImportRunSummary {
  fileName: string;
  fileSize: string;
  currentFile?: string;
  totalFiles?: number;
  uploadedFiles?: number;
}

export interface ImportRunState {
  active: boolean;
  progress: number;
  summary: ImportRunSummary | null;
  jobId: string | null;
  agencyId: string | null;
  cancelRequested: boolean;
}

const initialState: ImportRunState = {
  active: false,
  progress: 0,
  summary: null,
  jobId: null,
  agencyId: null,
  cancelRequested: false,
};

let state: ImportRunState = initialState;
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const patch = (next: Partial<ImportRunState>) => {
  state = { ...state, ...next };
  emit();
};

export const importRunStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot(): ImportRunState {
    return state;
  },
  isActive() {
    return state.active;
  },
  start(summary: ImportRunSummary, agencyId: string | null) {
    state = { active: true, progress: 0, summary, jobId: null, agencyId, cancelRequested: false };
    emit();
  },
  setActive(active: boolean) {
    if (active) {
      patch({ active: true, cancelRequested: false });
    } else {
      state = { ...initialState };
      emit();
    }
  },
  setProgress(progress: number) {
    patch({ progress });
  },
  setSummary(
    next: ImportRunSummary | null | ((prev: ImportRunSummary | null) => ImportRunSummary | null),
  ) {
    patch({ summary: typeof next === "function" ? next(state.summary) : next });
  },
  setJobId(jobId: string | null) {
    patch({ jobId });
  },
  requestCancel() {
    patch({ cancelRequested: true });
  },
  get cancelRequested() {
    return state.cancelRequested;
  },
};
